import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Col, Row, Collapse, Button } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import Papa from 'papaparse';
import { getContentValue } from '@truqc/content-path';
import autoMatchCSVColumns from './csvAutoMatch';
import Label from './label';
import formActionCreators from '../../../actions/formActionCreators';
import store from '../../../reducers/storeRegistration';
import config from '../../../config.json';
import entityComposition from '../../common/classes/entityComposition';
const rejectedSingleMessage = 'Only individual .CSV or .csv files are allowed, please retry your upload with a SINGLE valid file.';
const actionCreators = {
  formActionCreators // only form currently supports the editor cache
};
const buildEnvironment = config.env;
const validateHeaders = headers => {
  const validHeaders = [];
  const headerValidationWarnings = [];
  const singularHeaders = headers.filter(header => !header.includes('_'));
  headers.forEach(header => {
    // validate dupe headers
    if (header.includes('_') && header.split('_').length === 2) {
      const prefix = header.split('_')[0];
      const suffix = header.split('_')[1];
      if (singularHeaders.includes(prefix)) {
        const suffixNum = Math.floor(Number(suffix));
        if (suffixNum !== Infinity && String(suffixNum) === suffix && suffixNum >= 0) {
          headerValidationWarnings.push({
            rowIndex: 'Header',
            errorType: "Looks like there's more then one column named '".concat(prefix, "'"),
            actionTaken: "'_".concat(suffix, "' appended to extra column name"),
            message: "Consider renaming one of your '".concat(prefix, "' to something unique")
          });
        }
      }
    }
  });
  let missingColumnNames = 0;
  headers.forEach(header => {
    // validate empty string headers
    if (header === '') {
      missingColumnNames += 1;
    } else {
      validHeaders.push(header);
    }
  });
  if (missingColumnNames) {
    headerValidationWarnings.push({
      rowIndex: 'Header',
      errorType: 'Missing column name',
      actionTaken: "".concat(missingColumnNames, " column(s) will be ignored on import"),
      message: 'Give columns unique identifiers'
    });
  }
  return {
    headerValidationWarnings,
    validHeaders
  };
};
const extraRowColumnsParseKey = '__parsed_extra';
const SelectAndParseCSV = _ref => {
  let _ref$instructions = _ref.instructions,
    instructions = _ref$instructions === void 0 ? null : _ref$instructions,
    _ref$title = _ref.title,
    title = _ref$title === void 0 ? 'Upload CSV' : _ref$title,
    onChange = _ref.onChange,
    contentPath = _ref.contentPath,
    cachedVariableName = _ref.cachedVariableName,
    _ref$fileStatsContent = _ref.fileStatsContentPath,
    fileStatsContentPath = _ref$fileStatsContent === void 0 ? null : _ref$fileStatsContent,
    reduxEntity = _ref.reduxEntity,
    notificationActions = _ref.notificationActions;
  // console.log('props', props);
  // State to store table Column name
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    csvColumnHeaders = _useState2[0],
    setcsvColumnHeaders = _useState2[1];
  // State to control debug display
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    collapseRawData = _useState4[0],
    setCollapseRawData = _useState4[1];
  // State to store the values
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    values = _useState6[0],
    setValues = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    csvValidationErrors = _useState8[0],
    setCSVValidationErrors = _useState8[1];
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    const tmpFile = acceptedFiles[0];
    const parseErrors = [];

    // with the rejection handled below, this case will only get hit if there are
    // A: more then 1 csv file
    // B: more then 1 csv file AND at least 1 additional non csv file
    if (acceptedFiles.length > 1 || rejectedFiles.length) {
      notificationActions.error({
        message: rejectedSingleMessage
      });
      return;
    }
    if (tmpFile === undefined) return;
    await Papa.parse(tmpFile, {
      header: true,
      skipEmptyLines: true,
      complete: async _ref2 => {
        let data = _ref2.data;
        const headers = Object.keys(data[0]);
        const _validateHeaders = validateHeaders(headers),
          headerValidationWarnings = _validateHeaders.headerValidationWarnings,
          validHeaders = _validateHeaders.validHeaders;
        parseErrors.push(...headerValidationWarnings);

        // Filtered Column Names
        setcsvColumnHeaders(validHeaders);
        const headerObjects = {};
        validHeaders.forEach((head, index) => {
          headerObjects[camelCase(head)] = {
            title: head,
            _created: index,
            contentPath: null
          };
        });

        // Auto-match CSV columns to form fields using similarity matching
        const state = store.getState();
        const formState = state.form || {};
        const activeForm = formState.active || {};
        const formContent = activeForm.content || {};
        const formContentPath = getContentValue('/formContentPath', formContent);

        // Apply auto-matching with similarity threshold of 0.8 (80%)
        const matchedHeaderObjects = autoMatchCSVColumns(headerObjects, formContentPath, 0.8);
        const parsedData = [];
        data.forEach((row, index) => {
          const extraColumns = row[extraRowColumnsParseKey],
            emptyColumn = row[''],
            rest = _objectWithoutProperties(row, [extraRowColumnsParseKey, ""].map(_toPropertyKey));
          if (extraColumns) {
            parseErrors.push({
              rest,
              rowIndex: index + 1,
              // +1 for human readable
              extraColumns,
              errorType: 'Too many columns for row.',
              actionTaken: 'Extra columns ignored.'
            });
          }
          parsedData.push(Object.values(rest));
        });
        setValues(parsedData);
        await onChange({
          path: contentPath,
          value: matchedHeaderObjects,
          _created: new Date().toISOString()
        });
        await onChange({
          path: fileStatsContentPath,
          value: tmpFile.name
        });

        /*  Note - we're using a local client cache for data that we don't want to pass between
        *         client and server (could use 'transientContext' for that), and that we do NOT
        *         want to persist beyond the session (could use 'content' for that)
        */
        const entityActions = actionCreators["".concat(reduxEntity, "ActionCreators")];
        await store.dispatch(entityActions.setEditorCacheValue(cachedVariableName, data));
        setCSVValidationErrors(parseErrors);
      }
    });
  }, [contentPath, cachedVariableName, onChange, fileStatsContentPath, reduxEntity, notificationActions]);
  let columnMaxLength = 10;
  csvColumnHeaders.forEach(val => {
    if (val.length > columnMaxLength) columnMaxLength = val.length;
  });
  columnMaxLength += 2;
  const toggleShowRawData = () => setCollapseRawData(!collapseRawData);
  const validationErrorSection = csvValidationErrors.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    value: "CSV Parsing Errors"
  }), /*#__PURE__*/React.createElement("ul", null, csvValidationErrors.map(_ref3 => {
    let rowIndex = _ref3.rowIndex,
      _ref3$extraColumns = _ref3.extraColumns,
      extraColumns = _ref3$extraColumns === void 0 ? [] : _ref3$extraColumns,
      errorType = _ref3.errorType,
      actionTaken = _ref3.actionTaken,
      message = _ref3.message;
    return /*#__PURE__*/React.createElement("li", {
      key: rowIndex + uuidV4()
    }, /*#__PURE__*/React.createElement("pre", null, ["Problematic Row:  ".concat(rowIndex), "Error Type:       ".concat(errorType), "Action Taken:     ".concat(actionTaken), message ? "Message:          ".concat(message) : null].join('\n')), extraColumns.length ? /*#__PURE__*/React.createElement("pre", null, extraColumns.map(entity => "\u274C Extra Column: '".concat(entity, "'")).join('\n')) : null);
  }))) : null;

  // note - we could set this up for truqc admin users as well - detect the username also show
  const debugData = buildEnvironment !== 'beta' && buildEnvironment !== 'production' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    value: "CSV Uploaded and Parsed"
  }), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "debugDataButton",
    onClick: toggleShowRawData
  }, collapseRawData ? 'Hide' : 'Show', " parsed document"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("small", null, "Note that this data is only available on inital upload, not subsequent detail views"), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: collapseRawData
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement("h4", null, "Validated Column Headers: ", csvColumnHeaders.length, /*#__PURE__*/React.createElement("br", null)), /*#__PURE__*/React.createElement("h4", null, "Rows found: ", values.length), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("h3", null, "Validated Data"), /*#__PURE__*/React.createElement("ul", null, values.map(vals => /*#__PURE__*/React.createElement("li", {
    key: uuidV4()
  }, /*#__PURE__*/React.createElement("pre", null, vals && vals.length ? vals.map((v, i) => "".concat(csvColumnHeaders[i], ":").concat(Array(columnMaxLength - csvColumnHeaders[i].length).fill(' ').join('')).concat(v)).join('\n') : null)))), /*#__PURE__*/React.createElement("hr", null)))), validationErrorSection) : validationErrorSection;

  /* eslint-disable max-len */
  const _useDropzone = useDropzone({
      onDrop,
      accept: {
        'text/csv': ['.csv']
      }
    }),
    getRootProps = _useDropzone.getRootProps,
    getInputProps = _useDropzone.getInputProps;
  return csvColumnHeaders && csvColumnHeaders.length ? debugData : /*#__PURE__*/React.createElement("div", {
    className: "t-dud-dropzone"
  }, /*#__PURE__*/React.createElement(Label, {
    value: title
  }), /*#__PURE__*/React.createElement("div", _extends({}, getRootProps(), {
    className: "dropzone t-u-dropzone"
  }), /*#__PURE__*/React.createElement("input", _extends({
    alt: "file-input",
    "data-testid": "drop-input"
  }, getInputProps())), instructions || 'Drag a single csv file or click here to upload a single csv document to parse.'));
};
SelectAndParseCSV.propTypes = {
  title: PropTypes.string,
  reduxEntity: PropTypes.string.isRequired,
  instructions: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fileStatsContentPath: PropTypes.string,
  contentPath: PropTypes.string.isRequired,
  cachedVariableName: PropTypes.string.isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { SelectAndParseCSV as TestSelectAndParseCSV };
export default entityComposition(SelectAndParseCSV, [], {}, ['notification']);